import { CONFIG } from '../helper/config';
import { DeleteRequestInfo } from '../modal/narrative-board';
import { GetNotificationRequiredInfo, UserConfigModal } from '../modal/notification';
import { getRequestOptions, handleResponse, handleError, handleHTMLResponse } from './service-utilities';

/**
 * Service for managing notifications.
 *
 * Provides API calls to:
 * - Create, fetch, edit, and delete notifications
 * - Fetch cards and card HTML for notifications
 * - Retrieve home virtual assistants (VA) and their details
 */
export const notificationService = {
  /** Creates a new notification. */
  createNotification,

  /** Fetches notifications for a user or team. */
  getNotification,

  /** Updates an existing notification. */
  editNotification,

  /** Deletes a notification by ID. */
  deleteNotification,

  /** Fetches card data for a notification. */
  getCard,

  /** Fetches card HTML for a notification. */
  getCardHTML,

  /** Retrieves home virtual assistants data. */
  getHomeVas,

  /** Retrieves details for a specific virtual assistant. */
  getVADetails,
  updateAgentStatus,
};

/**
 * Creates a notification with the given info and returns the server response.
 * @param {Object} notification_info - The info to create a notification.
 * @param {string} auth_token - The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function createNotification(notification_info : any, auth_token:string){
  let url = CONFIG.API.BASEURL + CONFIG.API.BACKEND + CONFIG.API.REQUESTURL.CREATENOTIFICATION;
  let request_options : any = getRequestOptions(CONFIG.API.REQUESTMETHOD.POST, auth_token, JSON.stringify(notification_info));
  return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Fetches notifications for the given info and returns the server response.
 *
 * @param {GetNotificationRequiredInfo} info - The info object containing the necessary data for the request.
 * @param {string} auth_token - The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function getNotification(info : GetNotificationRequiredInfo, auth_token:string) {
    let url = CONFIG.API.BASEURL + CONFIG.API.BACKEND + CONFIG.API.REQUESTURL.GETNOTIFICATIONS;
    let request_options : any = getRequestOptions(CONFIG.API.REQUESTMETHOD.POST, auth_token, JSON.stringify(info));
    return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Updates a notification with the given identifier and returns the server response.
 *
 * @param {number} notification_id - The identifier of the notification to be updated.
 * @param {any} info - The information to update the notification with.
 * @param {string} auth_token - The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */

function editNotification(notification_id: number, info: any, auth_token:string) {
  let url =
    CONFIG.API.BASEURL +
    CONFIG.API.BACKEND +
    CONFIG.API.REQUESTURL.UPDATENOTIFICATIONS +
    notification_id;
  let request_options: any = getRequestOptions(
    CONFIG.API.REQUESTMETHOD.PUT,auth_token,
    JSON.stringify(info)
  );
  return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Deletes a notification with the given identifier and returns the server response.
 * @param {number} notification_id - The identifier of the notification to be deleted.
 * @param {DeleteRequestInfo} info - The request info containing the necessary identifiers.
 * @param {string} auth_token - The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function deleteNotification(notification_id : number, info : DeleteRequestInfo, auth_token:string) {
    let url = CONFIG.API.BASEURL + CONFIG.API.BACKEND + CONFIG.API.REQUESTURL.UPDATENOTIFICATIONS + notification_id;
    let request_options : any = getRequestOptions(CONFIG.API.REQUESTMETHOD.DELETE,auth_token, JSON.stringify(info))
    return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Fetches the card data for a specific notification and returns the server response.
 *
 * @param {number} notification_id - The identifier of the notification for which the card data is being fetched.
 * @param {UserConfigModal} user_config_keys - Keys required for accessing the card data.
 * @param {string} auth_token - The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function getCard(notification_id: number, user_config_keys:UserConfigModal, auth_token:string) {
  let url =
    CONFIG.API.BASEURL +
    CONFIG.API.BACKEND +
    CONFIG.API.REQUESTURL.GETCARD +
    notification_id;
    let request_options: any = getRequestOptions(CONFIG.API.REQUESTMETHOD.POST,auth_token,JSON.stringify(user_config_keys));
  return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Fetches the card HTML for a specific notification and returns the server response.
 *
 * @param {string} port_no - The port number to be passed to the service.
 * @param {any} info - The information required to fetch the card HTML.
 * @param {string} auth_token - The authentication token to use for the request.
 * @returns {Promise} A promise that resolves to the server response.
 */
function getCardHTML(port_no: string, info: any, auth_token:string) {
  let url =
    CONFIG.API.BASEURL +
    CONFIG.API.BACKEND +
    port_no +
    CONFIG.API.REQUESTURL.GETCARDHTML;
  let request_options: any = getRequestOptions(
    CONFIG.API.REQUESTMETHOD.POST,auth_token,
    JSON.stringify(info)
  );
  return fetch(url, request_options)
    .then(handleHTMLResponse)
    .catch(handleError);
}

/**
 * Calls getHomeVas service to get home virtual assistants data
 * 
 * @param {any} home_info - The request info containing tenant and team identifiers.
 * @param {string} auth_token - The authentication token for API requests.
 * 
 * If the result contains va_details, it dispatches an action to fetch the details of the first virtual assistant.
 * Dispatches an action to set the home virtual assistants data in the store.
 * Handles any errors that occur during the fetching process.
 */
function getHomeVas(home_info : any, auth_token:string){
  let url = CONFIG.API.BASEURL + CONFIG.API.BACKEND + CONFIG.API.REQUESTURL.GETHOMEVAS;
  let request_options : any = getRequestOptions(CONFIG.API.REQUESTMETHOD.POST, auth_token, JSON.stringify(home_info));
  return fetch(url, request_options).then(handleResponse).catch(handleError);
}

/**
 * Calls getVADetails service to get the details of a virtual assistant.
 * 
 * @param {any} info - The request info containing tenant and team identifiers, and the virtual assistant identifier.
 * @param {string} auth_token - The authentication token for API requests.
 * 
 * Dispatches an action to set the virtual assistant details in the store.
 * Handles any errors that occur during the fetching process.
 */
function getVADetails(info:any,auth_token:string){
  let url = CONFIG.API.BASEURL + CONFIG.API.BACKEND + CONFIG.API.REQUESTURL.GETVADETAILS ;
  let request_options : any = getRequestOptions(CONFIG.API.REQUESTMETHOD.POST, auth_token,JSON.stringify(info));
  return fetch(url, request_options).then(handleResponse).catch(handleError);
}

function updateAgentStatus(va_info:any,auth_token:string){
  let url = CONFIG.API.BASEURL + CONFIG.API.BACKEND +CONFIG.API.REQUESTURL.VIRTUALANALYSTBOTS + `${va_info?.va_id}` + CONFIG.API.REQUESTURL.UPDATEAGENTSTATUS ;
  let request_options : any = getRequestOptions(CONFIG.API.REQUESTMETHOD.POST, auth_token,JSON.stringify(va_info));
  return fetch(url, request_options).then(handleResponse).catch(handleError);
}